import React from "react";

import { styled } from "@mui/system";

import { Button, PropTypes } from "@/components/form/Button/Button";
import { SvgIcon } from "@/components/ui/svg-icon/SvgIcon";
import { SVGIconColor } from "@/components/ui/svg-icon/SvgIcon";
import { theme } from "@/theme/theme";
import { skipForwardProp } from "@/utils/skipForwardProp";
import { clsxm } from "@/utils/tailwindMerge";

type PaletteKey = keyof typeof theme.palette;

export type ButtonWithIconProps = Omit<PropTypes, "iconColor" | "sx"> & {
  Icon: React.ComponentType;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  isActive?: boolean;
  iconColor?: SVGIconColor;
  disabledHover?: boolean;
  iconHoverColor?: PaletteKey;
  className?: string;
  label?: string;
  children?: React.ReactNode;
  ariaLabel?: string;
  sx?: object;
};

export const ButtonWithIcon = React.forwardRef<
  HTMLButtonElement,
  ButtonWithIconProps
>(
  (
    {
      Icon,
      onClick,
      isActive = false,
      iconColor,
      disabledHover = false,
      iconHoverColor,
      className,
      ariaLabel,
      label,
      children,
      sx,
      ...rest
    },
    ref,
  ) => {
    return (
      <StyledButton
        onClick={onClick}
        ref={ref}
        variant="text"
        sx={sx}
        disabledHover={disabledHover}
        {...rest}
        className={clsxm("rounded-full", "w-8 h-8", className)}
        iconHoverColor={iconHoverColor}
        isActive={isActive}
        aria-label={ariaLabel}
      >
        <div className="flex flex-col items-center">
          <SvgIcon
            color={isActive ? "pink" : iconColor || "white"}
            className="w-[60%] h-[60%]"
          >
            <Icon />
          </SvgIcon>
          {label && (
            <span className="text-[11px] font-bold absolute top-8">
              {label}
            </span>
          )}
        </div>
        {children}
      </StyledButton>
    );
  },
);

const StyledButton = styled(Button, {
  shouldForwardProp: skipForwardProp([
    "disabledHover",
    "iconHoverColor",
    "isActive",
    "iconColor",
  ]),
})<
  Pick<
    ButtonWithIconProps,
    "disabledHover" | "iconHoverColor" | "isActive" | "iconColor"
  >
>(({ theme, disabledHover, iconHoverColor, isActive, iconColor }) => {
  const paletteColor = iconHoverColor
    ? theme.palette[iconHoverColor]
    : undefined;

  const hoverColor =
    typeof paletteColor === "object" && "main" in paletteColor
      ? paletteColor.main
      : undefined;

  return {
    "&:hover path": {
      fill: disabledHover ? "" : hoverColor,
    },
    "& path": {
      fill: isActive ? "pink" : iconColor,
    },
  };
});
ButtonWithIcon.displayName = "ButtonWithIcon";
